import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

interface LogoGenerateResponse {
  logoUrl: string;
  prompt: string;
  style: string;
}

interface UseLogoGeneratorResult {
  isGenerating: boolean;
  error: string | null;
  generateLogo: (
    prompt: string,
    style: string,
    projectTitle?: string
  ) => Promise<{ logoUrl: string; prompt: string } | null>;
  clearError: () => void;
}

/**
 * Hook for AI-powered logo generation
 */
export function useLogoGenerator(): UseLogoGeneratorResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Generate a logo using AI
   * @param prompt - Business description to base the logo on
   * @param style - Logo style (minimal, bold, elegant, playful, tech, organic)
   * @param projectTitle - Optional project title for context
   */
  const generateLogo = useCallback(
    async (
      prompt: string,
      style: string,
      projectTitle?: string
    ): Promise<{ logoUrl: string; prompt: string } | null> => {
      if (!prompt.trim()) {
        setError("Please provide a business description");
        return null;
      }

      setIsGenerating(true);
      setError(null);

      try {
        const functions = getFunctions();
        const generateLogoFunction = httpsCallable<
          { prompt: string; style: string; projectTitle?: string },
          LogoGenerateResponse
        >(functions, "generateLogo");

        const result = await generateLogoFunction({
          prompt,
          style,
          projectTitle,
        });

        return {
          logoUrl: result.data.logoUrl,
          prompt: result.data.prompt,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate logo";
        setError(errorMessage);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  return {
    isGenerating,
    error,
    generateLogo,
    clearError,
  };
}
